import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Import the main page components
import PatientDashboard from "./pages/PatientDashboard/PatientDashboard";
import StepPage from './components/StepPage';
import AppWrapper from './components/AppWrapper';
import DesignLayout from './components/DesignLayout';
import { ToastContainer } from 'react-toastify';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';

// Loading fallback component
const StepLoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '16px',
    color: '#666'
  }}>
    Loading step...
  </div>
);

// Lazy load step-specific components (Scan Process)
const PreparingScanStep = lazy(() => import('./pages/ScanProcess/PreparingScanStep'));
const AxesAlignmentStep = lazy(() => import('./pages/ScanProcess/AxesAlignmentStep'));
const FinalDesignPreviewStep = lazy(() => import('./pages/ScanProcess/FinalDesignPreviewStep'));

// Lazy load step-specific components (Main Process)
const RectificationStep = lazy(() => import('./pages/MainProcess/RectificationStep'));
const ElongationStep = lazy(() => import('./pages/MainProcess/ElongationStep'));
const TrimlineStep = lazy(() => import('./pages/MainProcess/TrimlineStep'));
const FlaresStep = lazy(() => import('./pages/MainProcess/FlaresStep'));
const ThicknessStep = lazy(() => import('./pages/MainProcess/ThicknessStep'));
const LockingMechanismStep = lazy(() => import('./pages/MainProcess/LockingMechanismStep'));
const AlignmentStep = lazy(() => import('./pages/MainProcess/AlignmentStep'));
const ValveStep = lazy(() => import('./pages/MainProcess/ValveStep'));
const PadsStep = lazy(() => import('./pages/MainProcess/PadsStep'));
const FinalizationStep = lazy(() => import('./pages/MainProcess/FinalizationStep'));

// Inner component that uses authentication
const AppContent = () => {
  const { isLoggedIn, login, loginRequired } = useAuth();

  // If login is not required, show the app
  if (!loginRequired) {
    return (
      <AppWrapper>
        <Routes>
          {/* Default redirect to dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" />} />

          {/* Patient Dashboard */}
          <Route path="/dashboard" element={<PatientDashboard />} />

          <Route path='/design' element={<DesignLayout />}>
            {/* Scan Process Routes */}
            <Route path="scan/preparing" element={<Suspense fallback={<StepLoadingFallback />}><PreparingScanStep /></Suspense>} />
            <Route path="scan/axes-alignment" element={<Suspense fallback={<StepLoadingFallback />}><AxesAlignmentStep /></Suspense>} />
            <Route path="scan/final-preview" element={<Suspense fallback={<StepLoadingFallback />}><FinalDesignPreviewStep /></Suspense>} />

            {/* Main Process Routes */}
            <Route path="main/rectification" element={<Suspense fallback={<StepLoadingFallback />}><RectificationStep /></Suspense>} />
            <Route path="main/elongation" element={<Suspense fallback={<StepLoadingFallback />}><ElongationStep /></Suspense>} />
            <Route path="main/trimline" element={<Suspense fallback={<StepLoadingFallback />}><TrimlineStep /></Suspense>} />
            <Route path="main/flares" element={<Suspense fallback={<StepLoadingFallback />}><FlaresStep /></Suspense>} />
            <Route path="main/thickness" element={<Suspense fallback={<StepLoadingFallback />}><ThicknessStep /></Suspense>} />
            <Route path="main/locking-mechanism" element={<Suspense fallback={<StepLoadingFallback />}><LockingMechanismStep /></Suspense>} />
            <Route path="main/alignment" element={<Suspense fallback={<StepLoadingFallback />}><AlignmentStep /></Suspense>} />
            <Route path="main/valve" element={<Suspense fallback={<StepLoadingFallback />}><ValveStep /></Suspense>} />
            <Route path="main/pads" element={<Suspense fallback={<StepLoadingFallback />}><PadsStep /></Suspense>} />
            <Route path="main/finalization" element={<Suspense fallback={<StepLoadingFallback />}><FinalizationStep /></Suspense>} />

            {/* Legacy route for backward compatibility */}
            <Route index element={<Navigate to="scan/preparing" />} />
          </Route>

          {/* Catch-all route */}
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </AppWrapper>
    );
  }

  // If not logged in, show login screen
  if (!isLoggedIn) {
    return <Login onLoginSuccess={login} />;
  }

  // If logged in, show the app
  return (
    <AppWrapper>
      <Routes>
        {/* Default redirect to dashboard */}
        <Route path="/" element={<Navigate to="/dashboard" />} />

        {/* Patient Dashboard */}
        <Route path="/dashboard" element={<PatientDashboard />} />

        <Route path='/design' element={<DesignLayout />}>
          {/* Scan Process Routes */}
          <Route path="scan/preparing" element={<Suspense fallback={<StepLoadingFallback />}><PreparingScanStep /></Suspense>} />
          <Route path="scan/axes-alignment" element={<Suspense fallback={<StepLoadingFallback />}><AxesAlignmentStep /></Suspense>} />
          <Route path="scan/final-preview" element={<Suspense fallback={<StepLoadingFallback />}><FinalDesignPreviewStep /></Suspense>} />

          {/* Main Process Routes */}
          <Route path="main/rectification" element={<Suspense fallback={<StepLoadingFallback />}><RectificationStep /></Suspense>} />
          <Route path="main/elongation" element={<Suspense fallback={<StepLoadingFallback />}><ElongationStep /></Suspense>} />
          <Route path="main/trimline" element={<Suspense fallback={<StepLoadingFallback />}><TrimlineStep /></Suspense>} />
          <Route path="main/flares" element={<Suspense fallback={<StepLoadingFallback />}><FlaresStep /></Suspense>} />
          <Route path="main/thickness" element={<Suspense fallback={<StepLoadingFallback />}><ThicknessStep /></Suspense>} />
          <Route path="main/locking-mechanism" element={<Suspense fallback={<StepLoadingFallback />}><LockingMechanismStep /></Suspense>} />
          <Route path="main/alignment" element={<Suspense fallback={<StepLoadingFallback />}><AlignmentStep /></Suspense>} />
          <Route path="main/valve" element={<Suspense fallback={<StepLoadingFallback />}><ValveStep /></Suspense>} />
          <Route path="main/pads" element={<Suspense fallback={<StepLoadingFallback />}><PadsStep /></Suspense>} />
          <Route path="main/finalization" element={<Suspense fallback={<StepLoadingFallback />}><FinalizationStep /></Suspense>} />

          {/* Legacy route for backward compatibility */}
          <Route index element={<Navigate to="scan/preparing" />} />
        </Route>

        {/* Catch-all route */}
        <Route path="*" element={<Navigate to="/dashboard" />} />
      </Routes>
    </AppWrapper>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
        <ToastContainer position="bottom-center" autoClose={3000} hideProgressBar={false} newestOnTop={true} closeOnClick pauseOnFocusLoss pauseOnHover/>
      </AuthProvider>
    </Router>
  );
}

export default App;